/** @see https://op.jinritemai.com/docs/guide-docs/1321/2978 */

import OrderDTO from "@/dto/order/OrderDTO";
import React, { Suspense, lazy, useEffect, useState } from "react";
import { Modal, message } from "antd";
import { observer } from "mobx-react-lite";
import { useGlobalStore } from "@/store/hooks";
import ShopType from "@/enum/ShopType";
import "./index.less";
import classNames from "classnames";
import { omit, uuid } from "@/utils";
import CopyButton from "@/components/CopyButton";
import UpdateConsigneeButton from "../UpdateConsigneeButton";
import Address, { joinTownAndStreet } from "../../Address";
import {
    getOriginalConsigneeAddress,
    getRealOrderOriginalId,
    hasLocalConsigneeInfo,
    isConsigneeExist,
} from "@/utils/OrderUtils";
import orderApi from "@/services/api/order";
import { cloneDeep } from "lodash";
import { useOrderManagementStore } from "@/scenes/OrderManagement/store/OrderManagementStore";
import { postLogOrderDecryptConsigneeByPlatformApi } from "@/services/api/order";

interface DouDianConsigneeProps {
    order: OrderDTO;
    onUpdateSuccess;
}

const BodyGuard = lazy(
    () =>
        new Promise<{ default: React.FC<any> }>((resolve, reject) => {
            import("@open-fxg/bodyguard-react").then((result) => resolve({ default: result.BodyGuard })).catch(reject);
        }),
);

const DouDianConsignee: React.FC<DouDianConsigneeProps> = function (props) {
    const { order, onUpdateSuccess } = props;
    const [desensitization, setDesensitization] = useState(false);
    // BodyGuard不会响应token的变化，所以当token变化时，重新构建该组件
    const [componentKey, updaterComponentKey] = useState("");
    const { consignee } = order;
    const globalStore = useGlobalStore();
    const shopId = order.shopId;
    const id = `address-${order.id}`;
    const orderManagementStore = useOrderManagementStore();

    const useLocalAddress = hasLocalConsigneeInfo(order.consignee);
    const containerRef = React.useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (globalStore.shopMapping.get(shopId)?.type !== ShopType.DOUDIAN) return;
        if (globalStore.DouDianSDKStore.mcTokenLoadingMapping.get(shopId)) return;

        globalStore.DouDianSDKStore.ensureMcToken(shopId);
    }, [shopId]);

    useEffect(() => {
        setDesensitization(false);
    }, [order.id]);

    /** toKen发生变化，修改收货人地址时，需要重新构建BodyGuard组件*/
    useEffect(() => {
        const key = globalStore.DouDianSDKStore.mcTokenMapping.get(shopId)?.token + order.id;

        updaterComponentKey(key);
    }, [globalStore.DouDianSDKStore.mcTokenMapping.get(shopId)?.token, order.id, order.consignee]);

    function handleUpdateSuccess() {
        onUpdateSuccess?.();

        setDesensitization(false);
        updaterComponentKey(uuid());
    }

    if (useLocalAddress) {
        const { localMobile: mobile, localTelephone: telephone } = consignee;

        let phone = mobile && telephone ? `${mobile} ${telephone}` : mobile || telephone;

        phone = phone ? phone + "，" : phone;

        return (
            <div>
                <span id={id}>
                    {consignee.localName ? `${consignee.localName}，` : null}
                    {phone}
                    {consignee.localProvince}
                    {consignee.localCity}
                    {consignee.localDistrict}
                    {consignee.localStreet}

                    <CopyButton
                        className="small text-light link-btn copyBtn mr5 ml5"
                        getElement={() => document.getElementById(`address-${order.id}`)}
                    />

                    <UpdateConsigneeButton
                        order={order}
                        onUpdateSuccess={handleUpdateSuccess}
                        className="small text-light link-btn updateBtn"
                    />
                </span>

                <p className="text-muted small">
                    原地址:
                    <Address
                        order={order}
                        shopType={ShopType.DOUDIAN}
                        replayMask={true}
                        onOrderUpdate={onUpdateSuccess}
                    />
                    <a
                        role="button"
                        tabIndex={-1}
                        className="text-muted link-btn"
                        style={{ userSelect: "none" }}
                        onClick={() => {
                            const shopId = order.shopId;

                            const address = getOriginalConsigneeAddress(order);

                            Modal.confirm({
                                content: `确定要使用原地址:${address} 吗?`,
                                async onOk() {
                                    try {
                                        const {
                                            provinceCode,
                                            cityCode,
                                            districtCode,
                                        } = await orderApi.deleteLocalConsignee({
                                            orderId: order.id,
                                            shopId,
                                        });

                                        const newOrder = cloneDeep(order);
                                        newOrder.provinceCode = provinceCode;
                                        newOrder.cityCode = cityCode;
                                        newOrder.districtCode = districtCode;
                                        const consigneeKey = Object.keys(newOrder.consignee);
                                        consigneeKey.forEach((val) => {
                                            if (val.indexOf("local") > -1) {
                                                newOrder.consignee = omit(newOrder.consignee, [val]);
                                            }
                                        });

                                        orderManagementStore?.table?.updateOrder(order.id, newOrder);

                                        onUpdateSuccess?.(order.id, newOrder);

                                        message.success("恢复使用原地址成功");
                                    } catch (e) {
                                        console.error(e);
                                        message.error("恢复使用原地址失败,请刷新页面后再试");
                                    }
                                },
                            });
                        }}
                    >
                        [使用原地址]
                    </a>
                </p>
            </div>
        );
    }

    const street = joinTownAndStreet(consignee.town, consignee.street);
    const { mobile, telephone } = consignee;

    let phone = mobile && telephone ? `${mobile} ${telephone}` : mobile || telephone;
    phone = phone ? phone + "，" : phone;

    const text = [
        consignee.name ? `${consignee.name}，` : null,
        phone,
        consignee.province,
        consignee.city,
        consignee.district,
        street,
    ]
        .filter(Boolean)
        .join("");

    async function handleDecryptedSuccess() {
        setDesensitization(true);

        await postLogOrderDecryptConsigneeByPlatformApi(shopId, [
            {
                id: order.id,
                originalId: order.originalId,
                mergeSplitStatus: order.mergeSplitStatus,
            },
        ]);
    }

    async function handleDecryptedFail({ message, code }) {
        if (code === 300106 || code === 300101) {
            globalStore.DouDianSDKStore.loadMcToken(order.shopId);

            Modal.error({
                title: "抖店解密失败",
                content: (
                    <div>
                        <p>请稍等一分钟重试一次</p>
                        <p>{message}</p>
                        <p>{code}</p>
                    </div>
                ),
            });

            return;
        }

        if (code === 998) {
            if (!containerRef.current) return;

            const reportId = `dd_buyer_report_700_${order.id}`;

            let reportEl = document.getElementById(reportId);

            if (!reportEl) {
                reportEl = document.createElement("span");
                reportEl.id = reportId;

                containerRef.current.appendChild(reportEl);
            }

            await globalStore.DouDianSDKStore.init700ReportCmp({
                element: reportEl,
                shopId: order.shopId,
                orderOriginalId: getRealOrderOriginalId(order),
            });

            return;
        }

        Modal.error({
            title: "抖店解密失败",
            content: (
                <div>
                    <p>{message}</p>
                    <p>{code}</p>
                </div>
            ),
        });
    }

    /** 收货人信息为空不展示操作按钮 */
    const showOperationBtn = isConsigneeExist(order.consignee);

    return (
        <div className="mb3">
            <div className={classNames("doudian-address")} id={id} ref={containerRef}>
                <Suspense fallback="加载中...">
                    <BodyGuard
                        key={componentKey}
                        token={globalStore.DouDianSDKStore.mcTokenMapping.get(shopId)?.token}
                        icon="https://img.alicdn.com/imgextra/i4/54458674/O1CN01g3sbMr2DwkLzUc9Lb_!!54458674.png"
                        text={text}
                        /* 设置解密后格式顺序 */
                        fields={["receiver_name", "phone", "receiver_address"]}
                        placement="right"
                        style={{ minWidth: "250px", width: "100%", position: "absolute" }}
                        innerStyle={{ fontSize: "13px" }}
                        orderId={getRealOrderOriginalId(order)}
                        onCopy={() => {
                            message.success("复制地址成功");
                        }}
                        success={handleDecryptedSuccess}
                        fail={handleDecryptedFail}
                    />
                </Suspense>

                {showOperationBtn ? (
                    <>
                        {!desensitization ? (
                            <CopyButton
                                className="small text-light link-btn copyBtn mr5"
                                getElement={() => document.getElementById(`address-${order.id}`)}
                            />
                        ) : null}

                        <UpdateConsigneeButton
                            order={order}
                            onUpdateSuccess={handleUpdateSuccess}
                            className="small text-light link-btn updateBtn"
                        />
                    </>
                ) : null}
            </div>
        </div>
    );
};

export default observer(DouDianConsignee);
